const WINDOWS_RESERVED_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\..*)?$/i;
const PROBLEMATIC_FILENAME_CHARACTERS = /[<>:"/\\|?*]/g;
const MAX_FILENAME_STEM_LENGTH = 120;

export const PLACEIO_MIME_TYPE = 'application/vnd.placeio.maps.project+zip';
export const LEGACY_DROPMAP_MIME_TYPE = 'application/vnd.dropmap.project+zip';
export const PROJECT_FILE_ACCEPT = `.placeio,.dropmap,${PLACEIO_MIME_TYPE},${LEGACY_DROPMAP_MIME_TYPE}`;

export function isDropmapFileName(fileName: string): boolean {
  return /\.(?:placeio|dropmap)$/i.test(fileName);
}

/** Browser-open identity: remove only the final supported extension, preserving other dots. */
export function projectTitleFromFileName(fileName: string, embeddedTitle: string): string {
  const stem = fileName.replace(/\.(?:placeio|dropmap)$/i, '').trim();
  // Browser filenames are normally <=255 characters; bound synthetic/untrusted File names too.
  return stem.slice(0, 512) || embeddedTitle;
}

function removeDropmapExtensions(value: string): string {
  let result = value;
  while (/\.(?:placeio|dropmap)$/i.test(result)) {
    result = result.replace(/\.(?:placeio|dropmap)$/i, '');
  }
  return result;
}

function trimTrailingDotsAndSpaces(value: string): string {
  return value.replace(/[. ]+$/g, '').trim();
}

function removeExtensions(value: string, extension: string): string {
  let result = value;
  const suffix = `.${extension}`;
  while (result.toLocaleLowerCase().endsWith(suffix.toLocaleLowerCase())) {
    result = result.slice(0, -suffix.length);
  }
  return result;
}

/** Creates a browser- and desktop-safe filename with exactly one extension. */
export function portableFilename(name: string, extension: string, fallbackStem: string): string {
  const withoutExtension = removeExtensions(name.trim(), extension);
  const sanitised = trimTrailingDotsAndSpaces(
    replaceProblematicFilenameCharacters(withoutExtension).replace(/-+/g, '-'),
  ).slice(0, MAX_FILENAME_STEM_LENGTH);
  const safeStem =
    !sanitised ||
    sanitised.replace(/[-_. ]/g, '').length === 0 ||
    WINDOWS_RESERVED_NAME.test(sanitised)
      ? fallbackStem
      : sanitised;
  return `${trimTrailingDotsAndSpaces(safeStem) || fallbackStem}.${extension}`;
}

function replaceProblematicFilenameCharacters(value: string): string {
  return [...value]
    .map((character) => (character.charCodeAt(0) < 32 ? '-' : character))
    .join('')
    .replace(PROBLEMATIC_FILENAME_CHARACTERS, '-');
}

export function dropmapFilenameForProject(projectTitle: string): string {
  return portableFilename(removeDropmapExtensions(projectTitle), 'placeio', 'Placeio project');
}
